import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { AuthService } from '../auth.service';

@Injectable({
  providedIn: 'root',
})
export class NoAuthGuard implements CanActivate, CanActivateChild, CanLoad {
  /**
   * Constructor
   */
  constructor(
    private _authService: AuthService,
    private _router: Router,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this._check(route);
  }

  /**
   * Can activate child
   *
   * @param childRoute
   * @param state
   */
  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this._check(childRoute);
  }

  /**
   * Can load
   *
   * @param route
   * @param segments
   */
  canLoad(
    route: Route,
    segments: UrlSegment[],
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this._check();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check the authenticated status
   *
   * @private
   */
  private _check(route?: ActivatedRouteSnapshot): Observable<boolean> {
    // Check if there's a token in the query parameters (for login-as-user functionality)
    const hasToken = route?.queryParamMap?.get('token');

    // If token is present, allow access regardless of authentication status
    if (hasToken) {
      return of(true);
    }

    // Check the authentication status
    return this._authService.authenticated$.pipe(
      switchMap((authenticated) => {
        // If the user is authenticated...
        if (authenticated) {
          // Extract checkout-related query params
          const productsParam = route?.queryParamMap?.get('products');
          const productParam = route?.queryParamMap?.get('product');
          const couponParam = route?.queryParamMap?.get('coupon');
          const yearlyParam = route?.queryParamMap?.get('yearly');

          // If checkout context exists (guest checkout with products),
          // redirect to order creation page for authenticated users
          if (productsParam || productParam) {
            this._router.navigate(['/checkout/create'], {
              queryParams: {
                products: productsParam,
                product: productParam,
                coupon: couponParam,
                yearly: yearlyParam
              }
            });
          } else {
            // No checkout context, redirect to dashboard
            this._router.navigate(['']);
          }

          // Prevent the access
          return of(false);
        }

        // Allow the access
        return of(true);
      }),
    );
  }
}
